/**
 * Header: .scroll class on scroll > 80, data-header-invert when hero in view,
 * data-header-lock when drawer open, logo SVG path split for reveal.
 */
import { getScrollTop } from './LenisController';
import type { ElementWithRevealInit } from '../../types';

const SCROLL_THRESHOLD = 80;

function updateHeaderScroll(): void {
  const header = document.querySelector('.no-header');
  if (!header) return;
  if (getScrollTop() > SCROLL_THRESHOLD) {
    header.classList.add('scroll');
  } else {
    header.classList.remove('scroll');
  }
}

function initHeaderInvert(): void {
  const root = document.documentElement;
  const hero = document.querySelector('.no-main-hero');

  const setInvert = (value: boolean): void => {
    if (root.getAttribute('data-header-lock') === 'true') return;
    if (value) root.setAttribute('data-header-invert', 'true');
    else root.removeAttribute('data-header-invert');
  };

  const inView = (el: Element): boolean => {
    const r = el.getBoundingClientRect();
    return r.top <= window.innerHeight && r.bottom >= 0;
  };

  const win = window;
  if (hero) {
    if (typeof win.IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver(
        (entries) => setInvert(entries[0].isIntersecting),
        { threshold: [0, 0.01, 0.5, 0.99, 1] }
      );
      io.observe(hero);
    } else {
      const update = (): void => setInvert(inView(hero));
      win.addEventListener('scroll', update, { passive: true });
      win.addEventListener('resize', update);
      win.lenis?.on?.('scroll', update);
      update();
    }
  }

  const mo = new MutationObserver(() => {
    const open = root.getAttribute('data-drawer-open') === 'true';
    if (open) root.setAttribute('data-header-lock', 'true');
    else {
      root.removeAttribute('data-header-lock');
      if (hero) setInvert(inView(hero));
    }
  });
  mo.observe(root, { attributes: true, attributeFilter: ['data-drawer-open'] });
}

function initLogoReveal(): void {
  const logoSvg = document.querySelector<SVGSVGElement & ElementWithRevealInit>('.no-header__logo a svg');
  if (!logoSvg || logoSvg.__svgRevealInit) return;

  const ns = 'http://www.w3.org/2000/svg';
  const paths = Array.from(logoSvg.querySelectorAll('path'));
  if (paths.length === 0) return;

  const gA = document.createElementNS(ns, 'g');
  gA.setAttribute('class', 'rd a');
  const gB = document.createElementNS(ns, 'g');
  gB.setAttribute('class', 'rd b');

  paths.forEach((p: SVGPathElement, i: number) => {
    const pa = p.cloneNode(true) as SVGPathElement;
    pa.style.setProperty('--i', String(i));
    const pb = p.cloneNode(true) as SVGPathElement;
    pb.style.setProperty('--i', String(i));
    gA.appendChild(pa);
    gB.appendChild(pb);
    p.remove();
  });

  logoSvg.appendChild(gA);
  logoSvg.appendChild(gB);

  const anchor = logoSvg.closest('a');
  if (anchor) anchor.classList.add('svg-reveal');

  logoSvg.__svgRevealInit = true;
}

function initUserDropdown(): void {
  const wrap = document.querySelector('[data-user-dropdown]');
  if (!wrap) return;
  const btn = wrap.querySelector<HTMLButtonElement>('.user-wrap__trigger');
  const menu = wrap.querySelector<HTMLElement>('.user-wrap__menu');
  if (!btn || !menu) return;

  const open = (): void => {
    menu.removeAttribute('hidden');
    btn.setAttribute('aria-expanded', 'true');
  };
  const close = (): void => {
    menu.setAttribute('hidden', '');
    btn.setAttribute('aria-expanded', 'false');
  };
  const toggle = (): void => {
    if (menu.hasAttribute('hidden')) open();
    else close();
  };

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  });
  document.addEventListener('click', () => {
    if (!menu.hasAttribute('hidden')) close();
  });
  wrap.addEventListener('click', (e) => e.stopPropagation());
}

export function initHeaderEffects(): void {
  updateHeaderScroll();
  initUserDropdown();

  const onScroll = (): void => {
    updateHeaderScroll();
  };

  if (window.lenis?.on) {
    window.lenis.on('scroll', onScroll);
  } else {
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  initHeaderInvert();
  initLogoReveal();
}
